import { alpha, Box, Grid, styled, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import CustomContainer from "../../../container";
import BestReviewedItems from "../../best-reviewed-items";
import FeaturedCategories from "../../featured-categories";
import PopularItemsNearby from "../../popular-items-nearby";
import RunningCampaigns from "../../running-campaigns";
import SpecialFoodOffers from "../../special-food-offers";
import Stores from "../../stores";
import FeaturedStores from "../pharmacy/featured-stores";
import CampaignBanners from "./CampaignBanners";
import FeaturedCategoriesWithFilter from "./FeaturedCategoriesWithFilter";
import NewArrivals from "./NewArrivals";
import { IsSmallScreen } from "../../../../utils/CommonValues";
import VisitAgain from "../../visit-again";
import LoveItem from "../../love-item";
import SinglePoster from "./SinglePoster";
import useGetOtherBanners from "../../../../api-manage/hooks/react-query/useGetOtherBanners";
import PharmacyStaticBanners from "../pharmacy/pharmacy-banners/PharmacyStaticBanners";
import OrderDetailsModal from "../../../order-details-modal/OrderDetailsModal";
import { useSelector } from "react-redux";
import { getToken } from "../../../../helper-functions/getToken";
import { getCurrentModuleId } from "../../../../helper-functions/getCurrentModuleType";

const SectionWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.3)
    : alpha(theme.palette.primary.main, 0.02),
  borderRadius: "24px",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderRadius: "16px",
  },
}));

const PromotionalSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const Shop = (props) => {
  const { configData } = props;
  const theme = useTheme();
  const menus = ["All", "Beauty", "Bread & Juice", "Drinks", "Milks"];
  const { orderDetailsModalOpen } = useSelector((state) => state.utilsData);
  const token = getToken();
  const { data, refetch, isLoading } = useGetOtherBanners();
  useEffect(() => {
    refetch();
  }, []);
  const visitedStores = localStorage.getItem("visitedStores")
    ? JSON.parse(localStorage.getItem("visitedStores"))?.length > 0
      ? JSON.parse(localStorage.getItem("visitedStores"))?.filter(
          (item) => item?.module_id === getCurrentModuleId()
        )
      : []
    : [];
  return (
    <Box sx={{ width: "100%", backgroundColor: theme.palette.background.default }}>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {/* HERO SECTION - Featured Categories */}
        <Grid item xs={12}>
          <HeroSection>
            <CustomContainer>
              <FeaturedCategories configData={configData} />
            </CustomContainer>
          </HeroSection>
        </Grid>

        {/* BANNERS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <PharmacyStaticBanners />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* QUICK ACCESS SECTION - Visit Again */}
        <Grid item xs={12}>
          <SectionWrapper>
            {IsSmallScreen() ? (
              <VisitAgain configData={configData} visitedStores={visitedStores} />
            ) : (
              <CustomContainer>
                <VisitAgain configData={configData} visitedStores={visitedStores} />
              </CustomContainer>
            )}
          </SectionWrapper>
        </Grid>

        {/* POPULAR PRODUCTS SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <PopularItemsNearby
                title="Most Popular Products"
                subTitle="We provide best quality & valuable products around the world"
              />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* CAMPAIGN BANNERS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <CampaignBanners />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* SPECIAL OFFERS SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <SpecialFoodOffers />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* FEATURED STORES SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <FeaturedStores title="Popular Store" configData={configData} />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* BEST REVIEWED SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <BestReviewedItems
                menus={menus}
                title="Best Reviewed Items"
                bannerIsLoading={isLoading}
                url={`${data?.promotional_banner_url}/${data?.best_reviewed_section_banner}`}
              />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* NEW ARRIVALS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <NewArrivals bannerData={data} />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* RUNNING CAMPAIGNS SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <RunningCampaigns />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* RECOMMENDED FOR YOU SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <LoveItem />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* FEATURED CATEGORIES WITH FILTER SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <FeaturedCategoriesWithFilter title="Featured Categories" />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* SINGLE POSTER SECTION */}
        <Grid item xs={12}>
          <PromotionalSection>
            <CustomContainer>
              <SinglePoster bannerData={data} />
            </CustomContainer>
          </PromotionalSection>
        </Grid>

        {/* ALL STORES SECTION */}
        <Grid item xs={12}>
          <SectionWrapper sx={{ paddingBottom: { xs: 4, md: 6 } }}>
            <CustomContainer>
              <Stores />
            </CustomContainer>
          </SectionWrapper>
        </Grid>
      {orderDetailsModalOpen && !token && (
        <OrderDetailsModal orderDetailsModalOpen={orderDetailsModalOpen} />
      )}
      </Grid>
    </Box>
  );
};

Shop.propTypes = {};

export default Shop;
